import { gameRules } from './gameRules';

export type ValidationKey =
  | 'error.multipleOf5'
  | 'error.minFifteen'
  | 'error.firstEntry'
  | 'error.tunnel200'
  | 'error.tunnel700'
  | 'error.overThousand'
  | 'warn.enterTunnel200'
  | 'warn.enterTunnel700';

type ValidationMessages = Record<ValidationKey, string>;

const messages: Record<'pl' | 'en', ValidationMessages> = {
  pl: {
    'error.multipleOf5': 'Wynik musi być podzielny przez 5',
    'error.minFifteen': 'Minimalny wynik to 15 (zasada trzech kości)',
    'error.firstEntry': 'Pierwszy wpis musi mieć co najmniej 50 punktów',
    'error.tunnel200': 'W tunelu 200-300 dodatni wynik musi wyjść (min. {need})',
    'error.tunnel700': 'W tunelu 700-800 dodatni wynik musi wyjść (min. {need})',
    'error.overThousand': 'Wynik przekroczyłby 1000 punktów',
    'warn.enterTunnel200': 'Wchodzisz do tunelu 200-300. Każdy dodatni rzut z wnętrza musi wyjść (min. {need}). Zero/karne mogą zostać.',
    'warn.enterTunnel700': 'Wchodzisz do tunelu 700-800. Każdy dodatni rzut z wnętrza musi wyjść (min. {need}). Zero/karne mogą zostać.',
  },
  en: {
    'error.multipleOf5': 'Score must be a multiple of 5',
    'error.minFifteen': 'Minimum score is 15 (three-dice rule)',
    'error.firstEntry': 'First entry must be at least 50 points',
    'error.tunnel200': 'In the 200-300 tunnel you must exit with a positive score (min. {need})',
    'error.tunnel700': 'In the 700-800 tunnel you must exit with a positive score (min. {need})',
    'error.overThousand': 'Score would exceed 1000 points',
    'warn.enterTunnel200': 'Entering tunnel 200-300. Any positive score inside must exit (min. {need}). Zero/penalties may stay.',
    'warn.enterTunnel700': 'Entering tunnel 700-800. Any positive score inside must exit (min. {need}). Zero/penalties may stay.',
  },
};

const tunnels = gameRules.tunnels;
const scoringRules = gameRules.scoring;

type ValidationMessageParams = Record<string, number>;

export interface ValidationOptions {
  locale?: 'pl' | 'en';
  translate?: (key: ValidationKey, params?: ValidationMessageParams) => string;
}

function renderMessage(key: ValidationKey, params: ValidationMessageParams | undefined, opts: ValidationOptions): string {
  if (opts.translate) {
    return opts.translate(key, params);
  }
  const dict = messages[opts.locale || 'pl'] || messages.pl;
  let template = dict[key] || key;
  if (params) {
    Object.keys(params).forEach((k) => {
      template = template.replace(`{${k}}`, String(params[k]));
    });
  }
  return template;
}

/**
 * Validates a score entry against game rules and tunnel constraints.
 */
export function validateScoreEntry(
  score: number,
  currentScore: number,
  _hasFirstEntry: boolean, // kept for call sites; rule oparty o currentScore
  canUseSingleDie: boolean = false,
  enableTunnels: boolean = true,
  options: ValidationOptions = {},
): { isValid: boolean; warning?: string; warningKey?: ValidationKey; error?: string; errorKey?: ValidationKey } {
  const render = (key: ValidationKey, params?: ValidationMessageParams) => renderMessage(key, params, options);
  if (score > 0 && score % 5 !== 0) {
    return { isValid: false, errorKey: 'error.multipleOf5', error: render('error.multipleOf5') };
  }
  if (score > 0 && score < scoringRules.minScoringDice.minPoints && !canUseSingleDie) {
    return { isValid: false, errorKey: 'error.minFifteen', error: render('error.minFifteen') };
  }
  if (!_hasFirstEntry && score > 0 && score < scoringRules.firstEntryMin) {
    return { isValid: false, errorKey: 'error.firstEntry', error: render('error.firstEntry') };
  }
  const newTotal = currentScore + score;
  const [z1, z2] = tunnels;
  const inPassage1 = enableTunnels && currentScore >= z1.min && currentScore < z1.max;
  const inPassage2 = enableTunnels && currentScore >= z2.min && currentScore < z2.max;
  if (enableTunnels && inPassage1 && score > 0 && newTotal < z1.max) {
    return { isValid: false, errorKey: 'error.tunnel200', error: render('error.tunnel200', { need: z1.max - currentScore }) };
  }
  if (enableTunnels && inPassage2 && score > 0 && newTotal < z2.max) {
    return { isValid: false, errorKey: 'error.tunnel700', error: render('error.tunnel700', { need: z2.max - currentScore }) };
  }
  if (newTotal > scoringRules.winScore) {
    return { isValid: false, errorKey: 'error.overThousand', error: render('error.overThousand') };
  }
  if (enableTunnels && (newTotal >= z1.min && newTotal < z1.max) && currentScore < z1.min) {
    return { isValid: true, warningKey: 'warn.enterTunnel200', warning: render('warn.enterTunnel200', { need: z1.max - newTotal }) };
  }
  if (enableTunnels && (newTotal >= z2.min && newTotal < z2.max) && currentScore < z2.min) {
    return { isValid: true, warningKey: 'warn.enterTunnel700', warning: render('warn.enterTunnel700', { need: z2.max - newTotal }) };
  }
  return { isValid: true };
}
